import { getRecoilStateValue } from "../../../Orchestrator/Recoils/Events/Recoil";
import type { IBookingResponse } from "../../../Orchestrator/Types/Index";
import type {
  IPaymentSessionAncillary,
  IPendingRefund,
} from "../../../Orchestrator/Recoils/AddonsAtoms";

/**
 * Returns the list of ancillaries to be REFUNDED as part of the
 * /api/payment/session call: items the user deselected during manage-
 * seating that are queued in pendingRefundFulfillmentIdsAtom.
 *
 * Backend treats every entry in this array as a refund target, so newly-
 * added PREBOOKED items must NOT be included — including them caused the
 * just-paid extras to be refunded immediately on settlement.
 *
 * Pure read — no atom writes, no side effects. Caller invokes this just
 * before building the request body.
 */
export function buildSessionAncillaries(
  bookingId: string | undefined,
): IPaymentSessionAncillary[] {
  if (!bookingId) return [];

  const pending = getRecoilStateValue(
    "pendingRefundFulfillmentIdsAtom",
  ) as IPendingRefund | null;
  if (!pending || pending.bookingId !== bookingId) return [];
  if (!pending.fulfillmentIds || pending.fulfillmentIds.length === 0) return [];

  const refundIdSet = new Set<string>(pending.fulfillmentIds);

  const bookingResp = getRecoilStateValue<IBookingResponse>("bookingResponseAtom");
  const booking = bookingResp?.booking;

  const ancillaries: IPaymentSessionAncillary[] = [];
  for (const offer of booking?.bookedOffers || []) {
    const offerId = (offer as any)?.offerId || "";
    // Skip offers without an offerId — backend can't act on a refund payload
    // that lacks a parent offer reference. Mirrors the ancId guard below.
    if (!offerId) continue;
    for (const anc of (offer as any)?.ancillaries || []) {
      const ancId = (anc as any)?.id;
      if (!ancId) continue;
      if (!refundIdSet.has(ancId)) continue;
      ancillaries.push({
        offerId,
        ancillaryOfferId: ancId,
        passengerRefs: (anc as any)?.passengerIds || [],
        fulfillmentId: ancId,
        bookingId,
      });
    }

    // Admissions too. An onward shuttle is an ADMISSION on its own booked
    // offer, not an ancillary — so replacing a paid shuttle has nothing to
    // refund unless admissions are emitted here. Without this the session goes
    // out with an empty refund list: the new shuttle is charged and the old one
    // is silently never refunded.
    //
    // `ancillaryOfferId` and `fulfillmentId` both carry the admission id,
    // mirroring the ancillary branch above where both carry the ancillary id.
    for (const admission of (offer as any)?.admissions || []) {
      const admissionId = (admission as any)?.id;
      if (!admissionId) continue;
      if (!refundIdSet.has(admissionId)) continue;
      ancillaries.push({
        offerId,
        ancillaryOfferId: admissionId,
        passengerRefs: (admission as any)?.passengerIds || [],
        fulfillmentId: admissionId,
        bookingId,
      });
    }
  }

  return ancillaries;
}
